import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../components/auth/AuthProvider';
import { useFavorites } from '../hooks/useFavorites';
import { useDemoPurchases } from '../hooks/useDemoPurchases';
import { getProductById, FirestoreProduct } from '../firebase/firestore';
import { ShoppingCartIcon, DownloadIcon, HeartIcon, MessageSquareIcon, SearchIcon, PackageIcon, StarIcon, XIcon, Loader2Icon, EyeIcon, PlayIcon, ArrowLeftIcon, CheckCircleIcon, FileIcon } from 'lucide-react';
import { DemoPurchase, DemoPurchaseItem } from '../hooks/useDemoPurchases';
export function BuyerDashboard() {
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('purchases');
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    favorites,
    toggleFavorite,
    favoritesCount,
    loading: favoritesLoading
  } = useFavorites();
  const {
    purchases,
    totalPurchasedItems
  } = useDemoPurchases(user?.id);
  const navigate = useNavigate();
  const location = useLocation();
  // State for fetched favorite products
  const [favoriteProducts, setFavoriteProducts] = useState<FirestoreProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<{
    order: DemoPurchase;
    item: DemoPurchaseItem;
  } | null>(null);
  const [downloadMessage, setDownloadMessage] = useState('');
  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    // Track last visited dashboard for login redirect
    localStorage.setItem('dgstack_last_dashboard', 'buyer');
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAuthenticated, user, navigate]);
  // Auto-select purchases tab when navigated from checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);
  // Fetch favorite products when favorites tab is active and favorites change
  useEffect(() => {
    if (activeTab !== 'favorites' || favorites.size === 0) {
      if (activeTab === 'favorites') setFavoriteProducts([]);
      return;
    }
    const fetchFavoriteProducts = async () => {
      setLoadingProducts(true);
      try {
        const productIds = Array.from(favorites);
        const products = await Promise.all(productIds.map(async id => {
          try {
            return await getProductById(id);
          } catch {
            return null;
          }
        }));
        setFavoriteProducts(products.filter((p): p is FirestoreProduct => p !== null));
      } catch (err) {
        console.error('Error fetching favorite products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchFavoriteProducts();
  }, [activeTab, favorites]);
  if (!isAuthenticated || !user) return null;
  const getInitials = () => {
    if (user.name) return user.name.charAt(0).toUpperCase();
    if (user.email) return user.email.charAt(0).toUpperCase();
    return 'U';
  };
  const renderEmptyState = (icon: React.ReactNode, title: string, description: string) => <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-3832">
      <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3833">
        {icon}
      </div>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-3834">
        {title}
      </h3>
      <p className="text-gray-500 dark:text-[#7A8F8A] max-w-md mx-auto" data-id="element-3835">
        {description}
      </p>
    </div>;
  const renderPurchases = () => {
    if (purchases.length === 0) {
      return renderEmptyState(<ShoppingCartIcon className="h-8 w-8" data-id="element-3836" />, 'No purchases yet', 'Your purchased products will appear here once you complete a transaction.');
    }
    return <div className="space-y-6" data-id="element-3837">
        {purchases.map(purchase => <div key={purchase.orderId} className="bg-white dark:bg-[#162825] rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330] overflow-hidden" data-id="element-3838">
            {/* Order header */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1A3330] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-id="element-3839">
              <div data-id="element-3840">
                <span className="text-sm font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3841">
                  {purchase.orderId}
                </span>
                <span className="text-xs text-gray-400 dark:text-[#7A8F8A] ml-3" data-id="element-3842">
                  {new Date(purchase.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
                </span>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400" data-id="element-3843">
                Completed
              </span>
            </div>

            {/* Order items */}
            <div className="divide-y divide-gray-100 dark:divide-[#1A3330]" data-id="element-3844">
              {purchase.items.map((item, idx) => <div key={`${purchase.orderId}-${idx}`} className="flex items-center gap-4 px-6 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1A3330]/50 transition-colors duration-150" onClick={() => setSelectedPurchase({
            order: purchase,
            item
          })} data-id="element-3845">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-[#1A3330] flex-shrink-0" data-id="element-3846">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" onError={e => {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80';
              }} data-id="element-3847" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3848">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-3849">
                      {item.title}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-0.5" data-id="element-3850">
                      Qty: {item.quantity}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-gray-800 dark:text-[#F8FAF9] flex-shrink-0" data-id="element-3851">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>)}
            </div>

            {/* Order total */}
            <div className="px-6 py-3 bg-gray-50 dark:bg-[#0F1F1D] flex justify-between items-center" data-id="element-3852">
              <span className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3853">
                Total
              </span>
              <span className="text-sm font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3854">
                ${purchase.total.toFixed(2)}
              </span>
            </div>
          </div>)}
      </div>;
  };
  const renderFavorites = () => {
    if (favoritesLoading || loadingProducts) {
      return <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-3855">
          <Loader2Icon className="h-8 w-8 animate-spin text-[#2DB489] dark:text-[#4ED1A0] mx-auto mb-4" data-id="element-3856" />
          <p className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-3857">
            Loading favorites...
          </p>
        </div>;
    }
    if (favoriteProducts.length === 0) {
      return <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm border border-gray-100 dark:border-[#1A3330]" data-id="element-3858">
          <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3859">
            <HeartIcon className="h-8 w-8" data-id="element-3860" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-3861">
            No favorites yet
          </h3>
          <p className="text-gray-500 dark:text-[#7A8F8A] max-w-md mx-auto mb-6" data-id="element-3862">
            Items you mark as favorite while browsing will be saved here.
          </p>
          <button onClick={() => navigate('/browse')} className="inline-flex items-center px-5 py-2.5 bg-[#2DB489] dark:bg-[#4ED1A0] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity" data-id="element-3863">
            <SearchIcon className="h-4 w-4 mr-2" data-id="element-3864" />
            Browse Products
          </button>
        </div>;
    }
    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-3865">
        {favoriteProducts.map(product => {
        const isVideo = product.isVideo || product.category === 'Short-form Video';
        const isVerticalVideo = product.videoMetadata?.isVertical || false;
        const sellerName = product.sellerName || 'Unknown';
        const rating = product.rating || 4.5;
        const isFree = product.price === 0 || product.isFree;
        return <div key={product.id} className="group relative bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full" data-id="element-3866">
              {/* Thumbnail — fixed aspect ratio */}
              <div className="relative aspect-[4/3] overflow-hidden cursor-pointer bg-black" onClick={() => navigate(`/product/${product.id}`)} data-id="element-3867">
                <img src={product.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'} alt={product.title} className="w-full h-full object-cover transition-transform duration-400 ease-in-out group-hover:scale-105" onError={e => {
              e.currentTarget.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80';
            }} data-id="element-3868" />
                {/* Video play indicator */}
                {isVideo && <div className="absolute inset-0 flex items-center justify-center" data-id="element-3869">
                    <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center" data-id="element-3870">
                      <PlayIcon className="h-6 w-6 text-white" data-id="element-3871" />
                    </div>
                  </div>}
                <button onClick={e => {
              e.stopPropagation();
              if (product.id) toggleFavorite(product.id);
            }} className="absolute top-3 right-3 bg-white dark:bg-[#162825] rounded-full p-2 shadow-md transition-all duration-200 opacity-100" aria-label="Remove from favorites" data-id="element-3872">
                  <HeartIcon className="h-5 w-5 text-red-500 fill-red-500 cursor-pointer" data-id="element-3873" />
                </button>
                {/* Category tag */}
                {product.category && <div className="absolute top-3 left-3" data-id="element-3874">
                    <span className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full" data-id="element-3875">
                      {product.category}
                    </span>
                  </div>}
                {/* Video Type indicator */}
                {isVideo && <div className="absolute bottom-3 left-3" data-id="element-3876">
                    <span className="bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-3877">
                      {isVerticalVideo ? 'Vertical' : 'Horizontal'} Video
                    </span>
                  </div>}
                {/* Free badge */}
                {isFree && !isVideo && <div className="absolute bottom-3 left-3" data-id="element-3878">
                    <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-3879">
                      FREE
                    </span>
                  </div>}
                {/* Trending badge */}
                {product.isTrending && <div className="absolute bottom-3 right-3" data-id="element-3880">
                    <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center" data-id="element-3881">
                      <span className="mr-1" data-id="element-3882">🔥</span>Trending
                    </span>
                  </div>}
                {/* New badge */}
                {product.isNewArrival && !product.isTrending && <div className="absolute bottom-3 right-3" data-id="element-3883">
                    <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center" data-id="element-3884">
                      <span className="mr-1" data-id="element-3885">✨</span>New
                    </span>
                  </div>}
              </div>
              {/* Content section — flex-1 pushes price row to bottom */}
              <div className="p-4 flex flex-col flex-1" data-id="element-3886">
                <div className="flex items-center justify-between mb-1.5" data-id="element-3887">
                  {/* Seller info */}
                  <div className="flex items-center" data-id="element-3888">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] flex items-center justify-center text-white text-xs mr-2 flex-shrink-0" data-id="element-3889">
                      {sellerName.charAt(0)}
                    </div>
                    <span className="text-xs text-gray-600 dark:text-[#7A8F8A] truncate" data-id="element-3890">
                      by {sellerName}
                    </span>
                  </div>
                  {/* Rating */}
                  <div className="flex items-center flex-shrink-0" data-id="element-3891">
                    <StarIcon className="h-3.5 w-3.5 text-yellow-400 fill-current" data-id="element-3892" />
                    <span className="text-xs text-gray-600 dark:text-[#7A8F8A] ml-1" data-id="element-3893">
                      {rating}
                    </span>
                  </div>
                </div>
                <h3 className="text-base font-semibold mb-1 text-gray-800 dark:text-[#F8FAF9] cursor-pointer line-clamp-1" onClick={() => navigate(`/product/${product.id}`)} data-id="element-3894">
                  {product.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mb-2.5 line-clamp-2 min-h-[2rem]" data-id="element-3895">
                  {product.shortDescription || product.description}
                </p>
                {/* Tags — fixed height to prevent layout shift */}
                <div className="flex flex-wrap gap-1.5 mb-2.5 min-h-[24px]" data-id="element-3896">
                  {product.platform && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-3897">
                      {product.platform}
                    </span>}
                  {product.format && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-3898">
                      {product.format}
                    </span>}
                  {product.skillLevel && <span className="text-xs bg-gray-100 dark:bg-[#1A3330] text-gray-600 dark:text-[#B0C1BD] px-1.5 py-0.5 rounded-full" data-id="element-3899">
                      {product.skillLevel}
                    </span>}
                </div>
                {/* Price + buttons — always at bottom */}
                <div className="flex items-center justify-between mt-auto" data-id="element-3900">
                  <span className="text-base font-bold text-gray-900 dark:text-white" data-id="element-3901">
                    {isFree ? 'Free' : `$${product.price}`}
                  </span>
                  <div className="flex space-x-1" data-id="element-3902">
                    <button className="bg-[#0A3C52] dark:bg-[#0B3C45] text-white px-2.5 py-1.5 rounded-l-full text-xs font-medium flex items-center justify-center hover:bg-[#0D4B65] transition-colors" onClick={() => navigate(`/product/${product.id}`)} data-id="element-3903">
                      <EyeIcon className="h-3 w-3 mr-1" data-id="element-3904" />
                      Preview
                    </button>
                    <button className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-2.5 py-1.5 rounded-r-full text-xs font-medium hover:bg-[#25A077] transition-colors" onClick={() => navigate(`/product/${product.id}`)} data-id="element-3905">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>;
      })}
      </div>;
  };
  const renderTabContent = () => {
    switch (activeTab) {
      case 'purchases':
        return renderPurchases();
      case 'downloads':
        return renderEmptyState(<DownloadIcon className="h-8 w-8" data-id="element-3906" />, 'No downloads available', 'Products you have purchased and can download will show up here.');
      case 'favorites':
        return renderFavorites();
      case 'messages':
        return renderEmptyState(<MessageSquareIcon className="h-8 w-8" data-id="element-3907" />, 'No messages', 'Your conversations with sellers will be listed here.');
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-[#F8FAFA] dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-3908">
      <Navbar scrolled={scrolled} data-id="element-3909" />

      <main className="pt-24 pb-16" data-id="element-3910">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3911">
          {/* Header */}
          <div className="mb-8" data-id="element-3912">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-[#F8FAF9] mb-2" data-id="element-3913">
              Buyer Dashboard
            </h1>
            <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-3914">
              Manage your purchases and downloads
            </p>
          </div>

          {/* Profile Card */}
          <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330] p-6 mb-8" data-id="element-3915">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6" data-id="element-3916">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-3917">
                <div className="w-20 h-20 rounded-full bg-[#2DB489] flex items-center justify-center text-white text-3xl font-bold flex-shrink-0" data-id="element-3918">
                  {getInitials()}
                </div>
                <div data-id="element-3919">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-3920">
                    {user.name || 'User'}
                  </h2>
                  <p className="text-gray-500 dark:text-[#7A8F8A] mb-4" data-id="element-3921">
                    {user.email}
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-3922">
                    <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-3923">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3924">
                        Total Purchases
                      </p>
                      <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3925">
                        {totalPurchasedItems}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-3926">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3927">
                        Total Downloads
                      </p>
                      <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3928">
                        0
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-2 rounded-lg" data-id="element-3929">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3930">
                        Favorites
                      </p>
                      <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3931">
                        {favoritesCount}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 w-full md:w-auto" data-id="element-3932">
                <button onClick={() => navigate('/browse')} className="w-full md:w-auto bg-white dark:bg-[#1A3330] border border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0] px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#2DB489]/5 dark:hover:bg-[#4ED1A0]/10 transition-all duration-300 flex items-center justify-center whitespace-nowrap" data-id="element-3933">
                  <SearchIcon className="h-4 w-4 mr-2" data-id="element-3934" />
                  Browse Products
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-8 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3935">
            <nav className="flex space-x-8 overflow-x-auto hide-scrollbar" data-id="element-3936">
              <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'purchases' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('purchases')} data-id="element-3937">
                <ShoppingCartIcon className="h-4 w-4 inline mr-2" data-id="element-3938" />
                My Purchases
              </button>
              <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'downloads' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('downloads')} data-id="element-3939">
                <DownloadIcon className="h-4 w-4 inline mr-2" data-id="element-3940" />
                Downloads
              </button>

              <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'favorites' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('favorites')} data-id="element-3941">
                <HeartIcon className="h-4 w-4 inline mr-2" data-id="element-3942" />
                Favorites{' '}
                {favoritesCount > 0 && <span className="ml-1 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs px-1.5 py-0.5 rounded-full" data-id="element-3943">
                    {favoritesCount}
                  </span>}
              </button>

              <button className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'messages' ? 'border-[#2DB489] dark:border-[#4ED1A0] text-[#2DB489] dark:text-[#4ED1A0]' : 'border-transparent text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]'} transition-colors duration-200`} onClick={() => setActiveTab('messages')} data-id="element-3944">
                <MessageSquareIcon className="h-4 w-4 inline mr-2" data-id="element-3945" />
                Messages
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div data-id="element-3946">{renderTabContent()}</div>
        </div>
      </main>

      <Footer data-id="element-3947" />

      {/* Purchase Detail Modal */}
      {selectedPurchase && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => {
      setSelectedPurchase(null);
      setDownloadMessage('');
    }} data-id="element-3948">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" data-id="element-3949" />

          {/* Modal */}
          <div className="relative bg-white dark:bg-[#162825] rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-[#1A3330]" onClick={e => e.stopPropagation()} data-id="element-3950">
            {/* Close button */}
            <button onClick={() => {
          setSelectedPurchase(null);
          setDownloadMessage('');
        }} className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-gray-100 dark:bg-[#1A3330] text-gray-500 dark:text-[#7A8F8A] hover:bg-gray-200 dark:hover:bg-[#0F1F1D] transition-colors" data-id="element-3951">
              <XIcon className="h-4 w-4" data-id="element-3952" />
            </button>

            {/* Product image */}
            <div className="w-full h-56 bg-gray-100 dark:bg-[#1A3330] overflow-hidden rounded-t-2xl" data-id="element-3953">
              <img src={selectedPurchase.item.image} alt={selectedPurchase.item.title} className="w-full h-full object-cover" onError={e => {
            e.currentTarget.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
          }} data-id="element-3954" />
            </div>

            {/* Content */}
            <div className="p-6" data-id="element-3955">
              {/* Status badge */}
              <div className="flex items-center gap-2 mb-3" data-id="element-3956">
                <CheckCircleIcon className="h-4 w-4 text-green-500" data-id="element-3957" />
                <span className="text-xs font-medium text-green-600 dark:text-green-400" data-id="element-3958">
                  Purchase Complete
                </span>
              </div>

              {/* Product name */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-[#F8FAF9] mb-4" data-id="element-3959">
                {selectedPurchase.item.title}
              </h3>

              {/* Order details grid */}
              <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-3960">
                <div className="bg-gray-50 dark:bg-[#0F1F1D] rounded-lg p-3" data-id="element-3961">
                  <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mb-0.5" data-id="element-3962">
                    Price
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-[#F8FAF9]" data-id="element-3963">
                    ${selectedPurchase.item.price.toFixed(2)}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-[#0F1F1D] rounded-lg p-3" data-id="element-3964">
                  <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mb-0.5" data-id="element-3965">
                    Quantity
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-[#F8FAF9]" data-id="element-3966">
                    {selectedPurchase.item.quantity}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-[#0F1F1D] rounded-lg p-3" data-id="element-3967">
                  <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mb-0.5" data-id="element-3968">
                    Order ID
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-[#F8FAF9] truncate" data-id="element-3969">
                    {selectedPurchase.order.orderId}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-[#0F1F1D] rounded-lg p-3" data-id="element-3970">
                  <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mb-0.5" data-id="element-3971">
                    Purchase Date
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-[#F8FAF9]" data-id="element-3972">
                    {new Date(selectedPurchase.order.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
                  </p>
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center py-3 border-t border-gray-100 dark:border-[#1A3330] mb-6" data-id="element-3973">
                <span className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3974">
                  Item Total
                </span>
                <span className="text-lg font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-3975">
                  $
                  {(selectedPurchase.item.price * selectedPurchase.item.quantity).toFixed(2)}
                </span>
              </div>

              {/* Download CTA */}
              <button onClick={() => {
            setDownloadMessage('This is a demo download. File delivery will be available after real payment integration is connected.');
            setTimeout(() => setDownloadMessage(''), 4000);
          }} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#2DB489]/20 transition-all duration-200" data-id="element-3976">
                <DownloadIcon className="h-5 w-5" data-id="element-3977" />
                Download Product (Demo)
              </button>

              {/* Download message */}
              {downloadMessage && <div className="mt-4 flex items-start gap-2.5 p-3 bg-blue-50 dark:bg-blue-900/15 border border-blue-100 dark:border-blue-800/30 rounded-lg" data-id="element-3978">
                  <FileIcon className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" data-id="element-3979" />
                  <p className="text-xs text-blue-700 dark:text-blue-300" data-id="element-3980">
                    {downloadMessage}
                  </p>
                </div>}

              {/* Back button */}
              <button onClick={() => {
            setSelectedPurchase(null);
            setDownloadMessage('');
          }} className="w-full mt-3 flex items-center justify-center gap-2 px-6 py-2.5 text-gray-600 dark:text-[#B0C1BD] hover:text-gray-800 dark:hover:text-[#F8FAF9] text-sm font-medium transition-colors" data-id="element-3981">
                <ArrowLeftIcon className="h-4 w-4" data-id="element-3982" />
                Back to My Purchases
              </button>
            </div>
          </div>
        </div>}
    </div>;
}